import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Building2, Heart, MapPin, MessageCircle } from "lucide-react";
import { useAuth, useLang } from "../App";
import { getCoverUrl, handlePhotoError } from "./PhotoManager";
import { amenityMeta, formatTZS, readHotels, storeHotels, trackWhatsAppClick, whatsappUrl } from "../lib/hotels";

// ================== SAVE HOTEL ==================
// "icon" renders a round heart for use over a photo; "full" is a labelled button.
export const SaveHotel = ({ hotel, variant = "full", className = "" }) => {
  const { user } = useAuth();
  const [saved, setSaved] = useState(() => readHotels("saved").some(h => h.id === hotel.id));
  useEffect(() => { setSaved(readHotels("saved").some(h => h.id === hotel.id)); }, [hotel.id, user]);
  useEffect(() => { const update = () => setSaved(readHotels("saved").some(h => h.id === hotel.id)); window.addEventListener("hotel-preferences", update); return () => window.removeEventListener("hotel-preferences", update); }, [hotel.id]);
  const toggle = () => storeHotels("saved", saved ? readHotels("saved").filter(h => h.id !== hotel.id) : [...readHotels("saved"), hotel]);

  if (variant === "icon") {
    return (
      <button
        type="button"
        aria-pressed={saved}
        aria-label={saved ? `Remove ${hotel.name} from saved hotels` : `Save ${hotel.name}`}
        onClick={toggle}
        className={`flex h-11 w-11 items-center justify-center rounded-full bg-white/90 shadow-sm backdrop-blur-sm transition-colors hover:bg-white ${saved ? "text-hs-amber" : "text-hs-charcoal"} ${className}`}
      >
        <Heart className="h-5 w-5" fill={saved ? "currentColor" : "none"} />
      </button>
    );
  }
  return (
    <button
      type="button"
      aria-pressed={saved}
      onClick={toggle}
      className={`btn-outline min-h-[44px] ${saved ? "border-hs-amber text-hs-amber" : ""} ${className}`}
    >
      {saved ? "♥ Saved Hotel" : "♡ Save Hotel"}
    </button>
  );
};

const PhotoFallback = () => (
  <div className="flex h-full w-full items-center justify-center bg-gradient-to-br from-hs-sage to-hs-border">
    <Building2 className="h-10 w-10 text-hs-muted" aria-hidden="true" />
  </div>
);

const HotelPhoto = ({ hotel }) => {
  const src = hotel.primary_photo_url || getCoverUrl(hotel.photos, "thumb") || hotel.cover_photo;
  if (!src) return <PhotoFallback />;
  return (
    <img
      src={src}
      alt={hotel.name}
      loading="lazy"
      onError={handlePhotoError}
      className="h-full w-full object-cover transition-transform duration-300 group-hover:scale-105"
    />
  );
};

const rating = hotel => Number(hotel.google_rating || hotel.average_rating || 0);

const Price = ({ hotel, lang, size = "lg" }) => hotel.min_price != null ? (
  <p className="text-hs-charcoal">
    <span className="text-sm text-hs-muted">{lang === "sw" ? "Kuanzia " : "From "}</span>
    <span className={`font-bold ${size === "lg" ? "text-lg" : "text-base"}`}>{formatTZS(hotel.min_price)}</span>
    <span className="text-sm text-hs-muted"> / night</span>
  </p>
) : (
  <p className="text-sm text-hs-muted">Contact for price</p>
);

const Amenities = ({ amenities = [], max = 4 }) => {
  if (!amenities.length) return null;
  const shown = amenities.slice(0, max);
  return (
    <ul className="flex flex-wrap gap-2" aria-label="Amenities">
      {shown.map(name => {
        const { icon, label } = amenityMeta(name);
        return <li key={name} className="rounded-lg bg-hs-sage px-2 py-1 text-xs text-hs-slate"><span aria-hidden="true">{icon}</span> {label}</li>;
      })}
      {amenities.length > max && <li className="rounded-lg bg-hs-sage px-2 py-1 text-xs font-medium text-hs-slate">+{amenities.length - max} more</li>}
    </ul>
  );
};

const WhatsAppButton = ({ hotel }) => hotel.whatsapp_number ? (
  <a
    href={whatsappUrl(hotel.whatsapp_number)}
    target="_blank"
    rel="noopener noreferrer"
    aria-label={`WhatsApp ${hotel.name}`}
    onClick={() => trackWhatsAppClick(hotel)}
    className="flex h-11 w-11 items-center justify-center rounded-full bg-green-500 text-white shadow-sm transition-colors hover:bg-green-600"
  >
    <MessageCircle className="h-5 w-5" />
  </a>
) : null;

// ================== HOTEL CARD ==================
const HotelCard = ({ hotel, layout = "grid", onSaveScroll }) => {
  const { lang } = useLang();
  const href = `/hotel/${hotel.id}`;
  const location = <><MapPin className="mr-1 inline h-3.5 w-3.5 -translate-y-px" aria-hidden="true" />{hotel.city}{hotel.address ? ` · ${hotel.address}` : ""}</>;
  const ratingBadge = rating(hotel) > 0 && (
    <span className="absolute left-3 top-3 rounded-full bg-black/60 px-2 py-1 text-xs font-medium text-white backdrop-blur-sm">
      ★ {rating(hotel).toFixed(1)}
    </span>
  );

  if (layout === "compact") {
    return (
      <Link to={href} className="group card block w-56 shrink-0 transition-shadow duration-200 hover:shadow-md" data-testid={`hotel-card-${hotel.id}`}>
        <div className="relative aspect-[16/10] overflow-hidden"><HotelPhoto hotel={hotel} /></div>
        <div className="p-3">
          <p className="line-clamp-1 font-semibold text-hs-charcoal">{hotel.name}</p>
          <p className="mb-1 text-sm text-hs-muted">{hotel.city}</p>
          <Price hotel={hotel} lang={lang} size="sm" />
        </div>
      </Link>
    );
  }

  if (layout === "list") {
    return (
      <article className="group card flex transition-shadow duration-200 hover:shadow-md" data-testid={`hotel-card-${hotel.id}`}>
        <div className="relative w-32 shrink-0 overflow-hidden sm:w-48">
          <Link to={href} onClick={onSaveScroll} className="block h-full min-h-[160px]" tabIndex={-1} aria-hidden="true"><HotelPhoto hotel={hotel} /></Link>
          {ratingBadge}
        </div>
        <div className="flex min-w-0 flex-1 flex-col gap-2 p-4">
          <div className="flex items-start justify-between gap-2">
            <div className="min-w-0">
              <Link to={href} onClick={onSaveScroll} className="line-clamp-1 text-base font-semibold text-hs-charcoal hover:text-hs-amber">{hotel.name}</Link>
              <p className="line-clamp-1 text-sm text-hs-muted">{location}</p>
            </div>
            <SaveHotel hotel={hotel} variant="icon" className="shrink-0 border border-hs-border" />
          </div>
          <Amenities amenities={hotel.amenities} />
          <div className="mt-auto flex flex-wrap items-end justify-between gap-3 pt-1">
            <Price hotel={hotel} lang={lang} />
            <div className="flex items-center gap-2">
              <WhatsAppButton hotel={hotel} />
              <Link to={href} onClick={onSaveScroll} className="inline-flex min-h-[44px] items-center rounded-xl border border-hs-amber px-4 text-sm font-semibold text-hs-amber transition-colors duration-150 hover:bg-hs-amber hover:text-white">View Details</Link>
            </div>
          </div>
        </div>
      </article>
    );
  }

  return (
    <article className="group card flex flex-col transition-shadow duration-200 hover:shadow-md" data-testid={`hotel-card-${hotel.id}`}>
      <div className="relative aspect-[16/10] overflow-hidden">
        <Link to={href} onClick={onSaveScroll} className="block h-full w-full" tabIndex={-1} aria-hidden="true"><HotelPhoto hotel={hotel} /></Link>
        {ratingBadge}
        <div className="absolute right-3 top-3 flex gap-2">
          <SaveHotel hotel={hotel} variant="icon" />
          <WhatsAppButton hotel={hotel} />
        </div>
      </div>
      <div className="flex flex-1 flex-col p-4">
        <Link to={href} onClick={onSaveScroll} className="mb-1 line-clamp-1 text-base font-semibold text-hs-charcoal hover:text-hs-amber">{hotel.name}</Link>
        <p className="mb-3 line-clamp-1 text-sm text-hs-muted">{location}</p>
        <div className="mb-3"><Amenities amenities={hotel.amenities} /></div>
        <div className="mb-4 mt-auto"><Price hotel={hotel} lang={lang} /></div>
        <Link
          to={href}
          onClick={onSaveScroll}
          className="flex min-h-[44px] w-full items-center justify-center rounded-xl border border-hs-amber py-2.5 text-sm font-semibold text-hs-amber transition-colors duration-150 hover:bg-hs-amber hover:text-white"
        >
          View Details
        </Link>
      </div>
    </article>
  );
};

export const HotelCardSkeleton = ({ layout = "grid" }) => layout === "list" ? (
  <div className="flex h-40 animate-pulse overflow-hidden rounded-2xl border border-hs-border bg-white" aria-hidden="true">
    <div className="w-32 bg-hs-sage sm:w-48" />
    <div className="flex-1 space-y-3 p-4"><div className="h-4 w-2/3 rounded bg-hs-sage" /><div className="h-3 w-1/2 rounded bg-hs-sage" /><div className="h-6 w-1/3 rounded bg-hs-sage" /></div>
  </div>
) : (
  <div className="animate-pulse overflow-hidden rounded-2xl border border-hs-border bg-white" aria-hidden="true">
    <div className="aspect-[16/10] bg-hs-sage" />
    <div className="space-y-3 p-4">
      <div className="h-4 w-3/4 rounded bg-hs-sage" />
      <div className="h-3 w-1/2 rounded bg-hs-sage" />
      <div className="flex gap-2"><div className="h-6 w-14 rounded-lg bg-hs-sage" /><div className="h-6 w-14 rounded-lg bg-hs-sage" /></div>
      <div className="h-5 w-2/5 rounded bg-hs-sage" />
      <div className="h-11 rounded-xl bg-hs-sage" />
    </div>
  </div>
);

export default HotelCard;
